import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Landmark, Briefcase, FileQuestion } from 'lucide-react';
import { dataRoomAPI, investmentAPI, fundAPI } from '../../services/api';

const LINK_MODES = [
  {
    key: 'fund',
    label: 'Fund',
    icon: Landmark,
    hint: 'Share with potential Investors evaluating this fund',
  },
  {
    key: 'investment',
    label: 'Investment / Deal',
    icon: Briefcase,
    hint: 'Share with potential Buyers evaluating this portfolio investment',
  },
  {
    key: 'other',
    label: 'Others',
    icon: FileQuestion,
    hint: 'Ad-hoc diligence for third parties — capture your own reason',
  },
];

export default function DataRoomForm() {
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [funds, setFunds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    link_type: 'fund',
    fund_id: '',
    investment_id: '',
    custom_reason: '',
    name: '',
    description: '',
    seed_default_folders: true,
  });
  const [nameEdited, setNameEdited] = useState(false);

  useEffect(() => {
    investmentAPI.getAll()
      .then(res => setInvestments(res.data || []))
      .catch(() => setInvestments([]));
    fundAPI.getAll()
      .then(res => setFunds(res.data || []))
      .catch(() => setFunds([]));
  }, []);

  const selectedFund = useMemo(
    () => funds.find(f => f.id === form.fund_id) || null,
    [funds, form.fund_id]
  );
  const selectedInvestment = useMemo(
    () => investments.find(i => i.id === form.investment_id) || null,
    [investments, form.investment_id]
  );

  // Auto-suggest a default data room name when the link changes (unless user edited it)
  useEffect(() => {
    if (nameEdited) return;
    let suggested = '';
    if (form.link_type === 'fund' && selectedFund) {
      suggested = `${selectedFund.name} — Fund Data Room`;
    } else if (form.link_type === 'investment' && selectedInvestment) {
      suggested = `${selectedInvestment.portfolio_company_name} — Diligence Room`;
    } else if (form.link_type === 'other' && form.custom_reason) {
      suggested = form.custom_reason.trim();
    }
    setForm(prev => ({ ...prev, name: suggested }));
  }, [form.link_type, selectedFund, selectedInvestment, form.custom_reason, nameEdited]);

  const setLinkType = (lt) => {
    setForm(prev => ({
      ...prev,
      link_type: lt,
      fund_id: lt === 'fund' ? prev.fund_id : '',
      investment_id: lt === 'investment' ? prev.investment_id : '',
      custom_reason: lt === 'other' ? prev.custom_reason : '',
      seed_default_folders: lt !== 'other',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.link_type === 'fund' && !form.fund_id) {
      alert('Please select a fund');
      return;
    }
    if (form.link_type === 'investment' && !form.investment_id) {
      alert('Please select an investment / deal');
      return;
    }
    if (form.link_type === 'other' && !form.custom_reason.trim()) {
      alert('Please describe the reason for this data room');
      return;
    }
    if (!form.name.trim()) {
      alert('Data room name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        link_type: form.link_type,
        fund_id: form.link_type === 'fund' ? form.fund_id : null,
        investment_id: form.link_type === 'investment' ? form.investment_id : null,
        custom_reason: form.link_type === 'other' ? form.custom_reason.trim() : null,
        name: form.name.trim(),
        description: form.description,
        seed_default_folders: form.seed_default_folders,
      };
      const res = await dataRoomAPI.create(payload);
      navigate(`/data-rooms/${res.data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create data room');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="data-room-form-page">
      <button
        onClick={() => navigate('/data-rooms')}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} className="mr-1" /> Back to Data Rooms
      </button>

      <div className="bg-white border rounded-lg p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-1">Create Data Room</h1>
        <p className="text-sm text-gray-500 mb-6">Scope this data room to a Fund, an Investment / Deal, or another purpose</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Mode selector (radio tabs) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data room is for *</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="link-type-tabs">
              {LINK_MODES.map(m => {
                const Icon = m.icon;
                const active = form.link_type === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setLinkType(m.key)}
                    className={`border rounded-lg p-3 text-left transition ${
                      active
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`link-type-${m.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={16} className={active ? 'text-blue-600' : 'text-gray-500'} />
                      <span className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-gray-800'}`}>
                        {m.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-snug">{m.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conditional link field */}
          {form.link_type === 'fund' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fund Name *</label>
              <select
                value={form.fund_id}
                onChange={e => setForm({ ...form, fund_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                required
                data-testid="fund-select"
              >
                <option value="">Select a fund...</option>
                {funds.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              {funds.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-1">No funds found. Create a fund in the Funds module first.</p>
              )}
            </div>
          )}

          {form.link_type === 'investment' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Investment Name *</label>
              <select
                value={form.investment_id}
                onChange={e => setForm({ ...form, investment_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                required
                data-testid="investment-select"
              >
                <option value="">Select an investment...</option>
                {investments.map(i => (
                  <option key={i.id} value={i.id}>{i.portfolio_company_name}</option>
                ))}
              </select>
              {investments.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-1">No investments found. Create one in the Investments module first.</p>
              )}
            </div>
          )}

          {form.link_type === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Purpose *</label>
              <input
                type="text"
                value={form.custom_reason}
                onChange={e => setForm({ ...form, custom_reason: e.target.value })}
                placeholder="e.g., Ad-hoc diligence for ABC Bank — Credit Assessment"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                required
                data-testid="custom-reason-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">A short descriptor of why this ad-hoc data room exists.</p>
            </div>
          )}

          {/* Data room name (auto-filled, editable) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Room Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => { setForm({ ...form, name: e.target.value }); setNameEdited(true); }}
              placeholder="e.g., Project Phoenix — Series C Due Diligence"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              required
              data-testid="name-input"
            />
            {!nameEdited && form.name && (
              <p className="text-[11px] text-gray-500 mt-1">Auto-suggested from the selection above — feel free to edit.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Short description of this data room..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="description-input"
            />
          </div>

          {form.link_type !== 'other' && (
            <div className="flex items-start gap-2">
              <input
                id="seed-folders"
                type="checkbox"
                checked={form.seed_default_folders}
                onChange={e => setForm({ ...form, seed_default_folders: e.target.checked })}
                className="rounded mt-0.5"
                data-testid="seed-folders-checkbox"
              />
              <label htmlFor="seed-folders" className="text-sm text-gray-700">
                {form.link_type === 'fund'
                  ? 'Seed with default folders (Fund Presentation, PPM, Fund Documents, Track Record, Team)'
                  : 'Seed with default folders (Presentation / Teaser, Financials, Legal, Commercial)'}
              </label>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/data-rooms')}
              className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              data-testid="submit-btn"
            >
              <Save size={14} className="mr-1" /> {saving ? 'Creating...' : 'Create Data Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
